import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { store } from 'init/redux-store';
import { mapToObj, omit } from 'remeda';
import type { GetUsersParams } from 'types/getUsersParams';
import type { Constants } from 'types/store/app-state';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const UserSchema = z
  .object({
    email: z.email(),
    firstName: z.string().nonempty(),
    id: z.number(),
    isActive: z.boolean(),
    lastName: z.string().nonempty(),
    profilePicture: z.url().nullable(),
    roles: z.array(z.string()),
    hasAcceptedGc: z.boolean(),
    preferences: z.object({
      itemsPerPage: z.number(),
      emails: z.object({
        daily: z.boolean(),
        weekly: z.boolean(),
        notifications: z.boolean(),
        tasks: z.boolean(),
      }),
    }),
  })
  .transform(data => {
    // https://github.com/colinhacks/zod/issues/2203
    const roles = data.roles.includes('admin') ? ['admin'] : [...data.roles];

    return {
      ...omit(data, ['firstName', 'lastName', 'roles']),
      roles,
      isAdmin: data.roles.includes('admin'),
      permissions: mapToObj((store.getState().app.constants as Constants).user_roles, role => [
        role.value,
        roles.includes(role.value),
      ]),
      name: {
        full: `${data.firstName} ${data.lastName}`,
        first: data.firstName,
        last: data.lastName,
        initials: `${data.firstName[0]}${data.lastName[0]}`,
      },
    };
  });

const UsersSchema = z.array(UserSchema);

export type User = z.infer<typeof UserSchema>;
type Users = z.infer<typeof UsersSchema>;

export async function loadUsersQueryFn(axiosInstance: Promise<AxiosInstance>, { queryKey }: any) {
  const instance = await axiosInstance;

  const { data } = await instance.get(
    `/users?${new URLSearchParams({
      showInactive: queryKey[1].loadInactiveUsers.toString(),
      fullResponse: 'true',
      sortBy: queryKey[1].params?.sort_by,
      sortOrder: queryKey[1].params?.sort_order,
    }).toString()}`,
  );

  return UsersSchema.parse(data);
}

const emptyUsers: Users = [];

export const useLoadUsers = (
  loadInactiveUsers: boolean = false,
  params?: GetUsersParams,
  options?: { isEnabled?: boolean },
) => {
  const { isEnabled = true } = options ?? {};
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Users, ApiError>({
    queryKey: ['users', { loadInactiveUsers, params }] as const,
    queryFn: context => loadUsersQueryFn(axiosInstance, context),
    placeholderData: emptyUsers,
    enabled: isEnabled,
  });

  return addResourceNameToQueryResult<Users, unknown, 'users', 'PreventUndefined'>(
    'users',
    queryResult,
    emptyUsers,
  );
};
